import React, { useEffect, useRef, useState } from "react";
import PaymentGateway, { PaymentGatewayRef } from "../../components/payment/PaymentGateway";
import { useLocation } from "react-router-dom";
import { getCreditPricing } from "../../utils/pricingConfig";

const PricingComparison = () => {
  const [credits, setCredits] = useState(50);
  const [currency, setCurrency] = useState<"INR" | "USD">("USD");

  const paymentGatewayRef = useRef<PaymentGatewayRef>(null);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const authToken =
    queryParams.get("authToken") || sessionStorage.getItem("authToken");
  const userEmail =
    queryParams.get("userEmail") || sessionStorage.getItem("userEmail");

  if (queryParams.get("userEmail")) {
    sessionStorage.setItem("userEmail", queryParams.get("userEmail")!);
  }
  let orgId = queryParams.get("orgId") || sessionStorage.getItem("orgId");
  if (queryParams.get("orgId")) {
    sessionStorage.setItem("orgId", queryParams.get("orgId")!);
  }

  const perCredit = getCreditPricing(currency);
  const totalAmount = (credits * perCredit).toFixed(2);
  const totalAmountValue = Number(totalAmount);
  const formIdForPayment = orgId ?? "credits-purchase";

  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const ipRes = await fetch("https://api.zynth.ai/api/v1/data/userprofile/ip-info");
        const ipData = await ipRes.json();
        const country = ipData?.country?.toUpperCase();
        if (country === "IN" || country === "INDIA") setCurrency("INR");
        else setCurrency("USD");
      } catch {
        // default stays USD
      }
    };
    detectCurrency();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <div className="w-full max-w-5xl mx-auto p-6">
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded-lg shadow-md border">
            <thead>
              <tr className="bg-gray-100">
                <th className="px-4 py-4 text-left font-semibold text-xl">API Product</th>
                <th className="px-4 py-4 text-left font-semibold text-xl">Buy Credits</th>
                <th className="px-4 py-4 text-left font-semibold text-xl">Enterprise</th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {/* ACTION ROW */}
              <tr>
                <td className="px-4 py-5 align-top">
                  <div className="font-semibold text-gray-900">Get Started</div>
                  <div className="text-sm text-gray-500">
                    Pay-as-you-go credits or custom enterprise partnership.
                  </div>
                </td>

                {/* Buy Credits action */}
                <td className="px-4 py-">
                  <div className="text-gray-800 mb-3">
                    Get 10 Free Credits on sign-up!
                    <br />
                    <br />
                    Perfect for exploring Zynth and integrating our APIs with pay-as-you-go credits.
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <button
                      className="px-3 py-1 bg-gray-200 rounded"
                      onClick={() => setCredits((p) => Math.max(10, p - 10))}
                    >
                      -
                    </button>
                    <input
                      type="number"
                      value={credits}
                      onChange={(e) => {
                        const val = Math.max(
                          10,
                          Math.round(Number(e.target.value) / 10) * 10
                        );
                        setCredits(val);
                      }}
                      className="border border-gray-300 rounded px-3 py-2 w-24 text-center"
                      min={10}
                      step={10}
                    />

                    <button
                      className="px-3 py-1 bg-gray-200 rounded"
                      onClick={() => setCredits((p) => p + 10)}
                    >
                      +
                    </button>
                  </div>

                  <div className="text-lg font-semibold mb-3">
                    {currency} {totalAmount}
                  </div>

                  {/* ✅ Replaced: Buy Credits -> Sign up */}
                  <button
                    className="bg-blue-600 text-white py-2 px-6 rounded"
                    onClick={() => window.open("https://developer.zynth.ai", "_blank")}
                  >
                    Sign up
                  </button>
                </td>

                {/* Enterprise action */}
                <td className="px-4 py-5">
                  <div className="text-gray-800 mb-3">
                    Tailored for platforms or companies needing advanced customization and scale.
                  </div>
                  <div className="text-gray-800 mt-3">
                    Get dedicated support, custom rate limits, and advanced build options.
                  </div>

                  <div className="border rounded-lg p-4 mb-3">
                    <div className="text-lg font-semibold">Custom pricing</div>
                  </div>
                  <a
                    href="https://zynth.ai/contact-us"
                    className="bg-blue-600 text-white py-2 px-6 rounded inline-block text-center"
                  >
                    Contact Us
                  </a>
                </td>
              </tr>

              {/* Pricing rows */}
              <tr>
                <td className="px-4 py-3 font-medium">Pricing</td>
                <td className="px-4 py-3">
                  {currency} {(perCredit * 10).toFixed(2)} / 10 Credits
                </td>
                <td className="px-4 py-3">Contact Us</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Presentation API</td>
                <td className="px-4 py-3">2 credits per Slide</td>
                <td className="px-4 py-3">Custom</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Slide Narrative API</td>
                <td className="px-4 py-3">2 credits per request</td>
                <td className="px-4 py-3">Custom</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Custom Slide Builder API</td>
                <td className="px-4 py-3">2 credits per request</td>
                <td className="px-4 py-3">Custom</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Rate Limit</td>
                <td className="px-4 py-3">10 requests/min</td>
                <td className="px-4 py-3">Custom</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Support</td>
                <td className="px-4 py-3">Email (24-hour SLA)</td>
                <td className="px-4 py-3">Dedicated success manager</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Custom Branding / Themes</td>
                <td className="px-4 py-3">Yes</td>
                <td className="px-4 py-3">Yes</td>
              </tr>

              <tr>
                <td className="px-4 py-3">Slide Template Programming</td>
                <td className="px-4 py-3">No</td>
                <td className="px-4 py-3">Yes</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Hidden Payment Gateway (kept in case you want it later) */}
        <PaymentGateway
          ref={paymentGatewayRef}
          authToken={authToken ?? ""}
          productinfo="Credits Purchase"
          isDiscounted={false}
          discountedAmount={0}
          originalAmount={totalAmountValue}
          formId={formIdForPayment}
          onSuccess={(result) => console.log("✅ Payment success", result)}
          onFailure={() => console.warn("❌ Payment failed or cancelled")}
        />
      </div>
    </div>
  );
};

export default PricingComparison;
